// UUID generation, fully client-side. v4 uses the native crypto.randomUUID();
// v7 (time-ordered) is assembled from a 48-bit millisecond timestamp + CSPRNG bits
// per the RFC 9562 layout.

export type UuidVersion = 'v4' | 'v7';

export interface UuidFormatOptions {
  uppercase: boolean;
  hyphens: boolean;
}

function toHex(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += bytes[i].toString(16).padStart(2, '0');
  return s;
}

function formatCanonical(hex32: string): string {
  return `${hex32.slice(0, 8)}-${hex32.slice(8, 12)}-${hex32.slice(12, 16)}-${hex32.slice(16, 20)}-${hex32.slice(20)}`;
}

/** RFC 9562 v4 — fully random (built from getRandomValues for portability). */
export function uuidV4(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // version 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx
  return formatCanonical(toHex(bytes));
}

/** RFC 9562 v7 — 48-bit Unix-ms timestamp prefix + random, time-sortable. */
export function uuidV7(): string {
  const ts = Date.now();
  const bytes = new Uint8Array(16);
  // 48-bit big-endian timestamp (use division for the bits above 2^32).
  bytes[0] = Math.floor(ts / 2 ** 40) & 0xff;
  bytes[1] = Math.floor(ts / 2 ** 32) & 0xff;
  bytes[2] = Math.floor(ts / 2 ** 24) & 0xff;
  bytes[3] = Math.floor(ts / 2 ** 16) & 0xff;
  bytes[4] = Math.floor(ts / 2 ** 8) & 0xff;
  bytes[5] = ts & 0xff;

  const rnd = crypto.getRandomValues(new Uint8Array(10));
  bytes.set(rnd, 6);

  bytes[6] = (bytes[6] & 0x0f) | 0x70; // version 7
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx

  return formatCanonical(toHex(bytes));
}

export function generateOne(version: UuidVersion): string {
  return version === 'v7' ? uuidV7() : uuidV4();
}

export function formatUuid(uuid: string, opts: UuidFormatOptions): string {
  let out = uuid;
  if (!opts.hyphens) out = out.replace(/-/g, '');
  if (opts.uppercase) out = out.toUpperCase();
  return out;
}

/** Generates `count` formatted UUIDs of the given version. */
export function generateUuids(
  version: UuidVersion,
  count: number,
  opts: UuidFormatOptions,
): string[] {
  if (count < 1) return [];
  const out: string[] = [];
  for (let i = 0; i < count; i++) {
    out.push(formatUuid(generateOne(version), opts));
  }
  return out;
}
